/**
 * FORGE-159: epic archiving / auto-hide visibility rules.
 *
 * Pure + dependency-free so the same rules drive the backend list filter AND the
 * frontend badges (no drift between "what the server hides" and "what the UI
 * labels"). Archiving never deletes data — it's purely a list-visibility flag;
 * every stats/insights/analytics query keeps reading the full row set.
 *
 * Two ways an epic leaves the default list:
 *  1. MANUAL archive — operator hides it. A reason is REQUIRED when the epic is
 *     not complete (so abandoned/stuck work is always annotated with "why").
 *  2. AUTO hide — a COMPLETE epic disappears EPIC_AUTO_HIDE_DAYS after it
 *     finished, with no action and no reason needed.
 */

export const EPIC_AUTO_HIDE_DAYS = 7;

/** States that count as "complete" (a successful terminal outcome). */
export const COMPLETE_STATES: ReadonlySet<string> = new Set([
  'COMPLETED',
  'COMPLETE',
  'DONE',
  'MERGED',
  'BUGFIX_COMPLETE',
  'INIT_COMPLETE',
]);

export function isComplete(state: unknown): boolean {
  return COMPLETE_STATES.has(String(state ?? '').toUpperCase());
}

/** Best timestamp marking when the epic completed (for the auto-hide clock). */
export function completionTime(epic: Record<string, any>): string | null {
  return (
    epic.completed_at ??
    epic.epic_completed_at ??
    epic.updated_at ??
    epic.created_at ??
    null
  );
}

/** Operator explicitly archived this epic. */
export function isManuallyArchived(epic: Record<string, any>): boolean {
  return epic.archived === true;
}

/** A complete epic whose completion is older than the auto-hide window. */
export function isAutoHidden(
  epic: Record<string, any>,
  now: number = Date.now(),
  days: number = EPIC_AUTO_HIDE_DAYS,
): boolean {
  if (isManuallyArchived(epic)) return false; // manual takes precedence in labeling
  if (!isComplete(epic.state)) return false;
  const t = Date.parse(completionTime(epic) ?? '');
  if (Number.isNaN(t)) return false;
  return now - t >= days * 86_400_000;
}

/** Hidden from the default epics list (manual archive OR auto-hide). */
export function isHidden(
  epic: Record<string, any>,
  now: number = Date.now(),
  days: number = EPIC_AUTO_HIDE_DAYS,
): boolean {
  return isManuallyArchived(epic) || isAutoHidden(epic, now, days);
}

/** A reason is required to MANUALLY archive an epic that isn't complete. */
export function requiresArchiveReason(epic: Record<string, any>): boolean {
  return !isComplete(epic.state);
}

export interface ArchiveVisibility {
  /** Hidden from the default list. */
  hidden: boolean;
  /** Operator-archived (vs auto). */
  archived: boolean;
  /** Complete + past the window (no manual archive). */
  autoHidden: boolean;
  archiveReason: string | null;
  archivedAt: string | null;
  archivedBy: string | null;
}

/** Full visibility descriptor for badging + the API summary. */
export function archiveVisibility(
  epic: Record<string, any>,
  now: number = Date.now(),
  days: number = EPIC_AUTO_HIDE_DAYS,
): ArchiveVisibility {
  const archived = isManuallyArchived(epic);
  const autoHidden = isAutoHidden(epic, now, days);
  return {
    hidden: archived || autoHidden,
    archived,
    autoHidden,
    archiveReason: typeof epic.archive_reason === 'string' ? epic.archive_reason : null,
    archivedAt: typeof epic.archived_at === 'string' ? epic.archived_at : null,
    archivedBy: typeof epic.archived_by === 'string' ? epic.archived_by : null,
  };
}
